import { useTolgee } from '@tolgee/react';
import {
  useTrackOpportunityDetailsTeaserPreviewCloseMutation,
  useTrackOpportunityDetailsTeaserPreviewOpenMutation,
  useTrackOpportunityDetailsTeaserPreviewPageChangeMutation
} from 'api/analitics';
import { useGetEducationMaterialBySearchQuery } from 'api/estating-academy';
import { useGetPropertyByIdQuery } from 'api/property-teaser';
import { getTeaserDownloadUrl } from 'api/teaser';
import Loading from "components/Loading";
import { useSession } from 'context/session';
import { useQuery } from 'hooks/use-query';
import { AdditionalMaterials } from 'new-architecture/modules/additional-materials';
import { AnchorProvider, AnchorSection } from 'new-architecture/modules/anchor-navigation';
import { HeaderContext, SubheaderPortal } from 'new-architecture/modules/header';
import { PropertyDataRoom } from 'new-architecture/modules/property-data-room';
import { PropertyDisclaimer } from 'new-architecture/modules/property-disclaimer';
import { PropertyEducationAndResearch } from 'new-architecture/modules/property-education-and-research';
import { NavigationBar, PropertyInvestment } from 'new-architecture/modules/property-investment';
import { InvestmentOpportunityLocation } from 'new-architecture/modules/property-investment/components/investment-opportunity-location';
import { PropertyPartner } from 'new-architecture/modules/property-partner';
import { PropertyPartners } from 'new-architecture/modules/property-partners';
import { PropertyPerformanceReport } from 'new-architecture/modules/property-performance-report';
import { PropertyRiskLevel } from 'new-architecture/modules/property-risk-level';
import { PropertySecurityInfo } from 'new-architecture/modules/property-security-info';
import { PropertySubHeader } from 'new-architecture/modules/property-sub-header';
import { TeaserPreview } from 'new-architecture/modules/teaser-preview';
import { FC, memo, useCallback, useContext, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from "react-router-dom";
import { PropertyTeaserType } from 'store/properties/types';
import { v4 } from 'uuid';
import { PropertySectionAnchor } from '../../constants/property-section-anchor';
import { getNonEmptySections, isEmptySection } from '../../helpers/section-validation';
import NoAvailable from 'new-architecture/components/no-available';
import classNames from 'classnames';

import styles from './style.module.scss';

export const Property: FC<{}> = memo(() => {
  const { id } = useParams<{ id: string }>();
  const { containerHeight } = useContext(HeaderContext);
  const { user, whoAreYou } = useSession();
  const tolgee = useTolgee();

  const { data: property, isLoading } = useGetPropertyByIdQuery({ id, params: `lang=${tolgee.getPendingLanguage()}` });
  const { data: educationMaterial, isLoading: isMaterialLoading } = useGetEducationMaterialBySearchQuery({ 
    whoAreYou: user?.whoAreYou._value!,
    tags: property?.tags!
  });

  const helmet = useMemo(() => {
    if (!property) {
      return null;
    }

    return (
      <Helmet>
        <title>{`${property.name} - Estating`}</title>
        {property.description && 
          <meta name="description" content={`${property.description.name} | ${property.description.organisation}`} />
        }
      </Helmet>
    )
  }, [property]);

  const sectionsDescription: Array<{ id: PropertySectionAnchor, section: JSX.Element }> = useMemo(() => {
    if (!property) {
      return [];
    }

    return [
      {
        id: PropertySectionAnchor.INVESTMENT,
        section: <PropertyInvestment property={property} />
      },
      {
        id: PropertySectionAnchor.PARTNER,
        section: <PropertyPartner property={property} />,
      },
      {
        id: PropertySectionAnchor.LOCATION,
        section: <InvestmentOpportunityLocation property={property} />,
      },
      {
        id: PropertySectionAnchor.RISK,
        section: <PropertyRiskLevel property={property} />
      },
      {
        id: PropertySectionAnchor.ROLES,
        section: <PropertyPartners property={property} />
      },
      {
        id: PropertySectionAnchor.SECURITY_INFORMATION,
        section: <PropertySecurityInfo property={property} />
      },
      {
        id: PropertySectionAnchor.DATA_ROOM,
        section: <PropertyDataRoom property={property} />
      },
      {
        id: PropertySectionAnchor.INVESTOR_REPORT,
        section: <PropertyPerformanceReport property={property} />
      },
      {
        id: PropertySectionAnchor.EDUCATION_AND_RESEARCH,
        section: <PropertyEducationAndResearch property={property} materials={property.academyMaterials}/>
      },
      {
        id: PropertySectionAnchor.DISCLAIMER,
        section: <PropertyDisclaimer disclaimers={property.disclaimers.map(({ value }) => value)} property={property} />
      },
      {
        id: PropertySectionAnchor.ADDITIONAL_MATERIALS,
        section: <AdditionalMaterials property={property} />
      }
    ]
  }, [property]);

  const query = useQuery();
  const history = useHistory();

  const previewedTeaserData = useMemo(() => {
    const previewedTeaserId = query.get('teaser-preview');
    if (!previewedTeaserId) {
      return null;
    }

    return {
      teaserUrl: getTeaserDownloadUrl(previewedTeaserId),
    }
  }, [query]);

  const onClosePreview = useCallback(() => {
    query.delete('teaser-preview');
    history.push({ search: query.toString() });
  }, [history, query]);

  const sessionId = useRef(v4());

  const generateNewSessionId = useCallback(() => {
    sessionId.current = v4();
  }, []);

  const [trackTeaserOpen] = useTrackOpportunityDetailsTeaserPreviewOpenMutation();
  const [trackTeaserClose] = useTrackOpportunityDetailsTeaserPreviewCloseMutation();
  const [trackTeaserPageChange] = useTrackOpportunityDetailsTeaserPreviewPageChangeMutation();

  const onTeaserPreviewOpen = useCallback((teaser: PropertyTeaserType, page: number) => {
    trackTeaserOpen({
      opportunity: teaser.name,
      opportunityId: teaser._id,
      page: String(page),
      sessionId: sessionId.current
    });
  }, [trackTeaserOpen]);

  const onTeaserPreviewClose = useCallback((teaser: PropertyTeaserType, page: number) => {
    trackTeaserClose({
      opportunity: teaser.name,
      opportunityId: teaser._id,
      page: String(page),
      sessionId: sessionId.current
    });

    generateNewSessionId();
  }, [generateNewSessionId, trackTeaserClose]);

  const onTeaserPreviewPageChange = useCallback((teaser: PropertyTeaserType, page: number) => {
    trackTeaserPageChange({
      opportunity: teaser.name,
      opportunityId: teaser._id,
      page: String(page),
      sessionId: sessionId.current
    });
  }, [trackTeaserPageChange]);

  if (isLoading || isMaterialLoading) {
    return <Loading/>
  }

  if (!property || !educationMaterial) {
    return <main className={classNames(styles['main_100-vh-header'])}><NoAvailable /></main>;
  }

  return (
    <>
      {helmet}
      <AnchorProvider offset={containerHeight}>
        <SubheaderPortal>
          <PropertySubHeader property={property} />
          <NavigationBar anchors={getNonEmptySections(property, whoAreYou)} />
        </SubheaderPortal>

        <main>
          {sectionsDescription.filter(section => !isEmptySection(section.id, property, whoAreYou)).map(({ id, section }) => (
            <AnchorSection
              id={id}
              style={{ scrollMarginTop: `${containerHeight - 1}px` }}
              key={id}
            >
              {section}
            </AnchorSection>
          ))}
        </main>
        
        {
          previewedTeaserData &&
          <TeaserPreview
            isOpen={!!previewedTeaserData}
            onClose={onClosePreview}
            file={previewedTeaserData.teaserUrl}
            mimeType={'application/pdf'}
            title={''}

            property={property}
            onPropertyClose={onTeaserPreviewClose}
            onPropertyOpen={onTeaserPreviewOpen}
            onPropertyPageChange={onTeaserPreviewPageChange}
          />
        }
      </AnchorProvider>
    </>
  );
});

